import Table from "@/v2-backoffice-client/components/table"
import type {
  UptimeBucket,
  UptimeBucketCounts,
} from "@/v2-backoffice-client/uptime/uptime.buckets.utils"

export type UptimeStatusFilterProps = {
  // Pre-computed per-bucket counts. The board does the single-pass
  // count once for the whole fleet so this component is purely
  // presentational; the underlying `Table.StatusFilter` reads `count`
  // off each option to render the badge.
  counts: UptimeBucketCounts
  // URL key used by `Table.StatusFilter` to persist the active
  // bucket to the address bar so deep-linking works ("?…=down").
  // Two surfaces -- the incidents view and the per-category view --
  // each pick their own key so a saved URL targets exactly one of
  // them.
  urlParam: "incidentStatus" | "categoryStatus"
  // Fired whenever the operator picks a pill. The bucket name is
  // the `filterValue` registered on each option ("down" / "slow" /
  // "drift" / "muted") or "all" when the user clicks the All pill
  // (which has no `filterValue`).
  onChange(bucket: UptimeBucket): void
}

// Shared segmented status filter for the two uptime tables (incident
// center, per-category page). Both surfaces showed identical pills
// (All / Down / Slow / Drift / Muted) backed by identical bucket
// logic; pulling them into one component means a tweak to the icon
// or count formatting lands once instead of in two places, and the
// counts the table renders always agree with the bucket the table
// filters on (because the same component owns both).
function UptimeStatusFilter(props: Readonly<UptimeStatusFilterProps>) {
  const { counts, urlParam, onChange } = props

  return (
    <Table.StatusFilter
      // No `column` prop -- callers drive `data` from React state
      // because OR-style bucketing doesn't fit a single-eq column
      // filter (a target can be both "down" and "drift", which a
      // column eq can't represent).
      defaultValue={null}
      urlParam={urlParam}
      onChange={(_value, filterValue) => {
        // Bucket on the *filterValue string*, not the numeric pill
        // index -- the registered filterValue is what the URL stores
        // and what `matchesUptimeBucket` switches on. (`?? "all"`
        // covers the "All" pill where filterValue is null because
        // no `filterValue` prop was passed.)
        onChange((filterValue as UptimeBucket | null) ?? "all")
      }}
    >
      <Table.StatusFilter.Option
        value={null}
        filterValue="all"
        count={counts.all}
      >
        All
      </Table.StatusFilter.Option>
      <Table.StatusFilter.Option
        value={1}
        filterValue="down"
        count={counts.down}
        iconSrc="triangle-warning"
        iconClassName="text-destructive"
      >
        Down
      </Table.StatusFilter.Option>
      <Table.StatusFilter.Option
        value={2}
        filterValue="slow"
        count={counts.slow}
        iconSrc="clock"
        iconClassName="text-amber-500"
      >
        Slow
      </Table.StatusFilter.Option>
      <Table.StatusFilter.Option
        value={3}
        filterValue="drift"
        count={counts.drift}
        iconSrc="swap"
        iconClassName="text-violet-500"
      >
        Drift
      </Table.StatusFilter.Option>
      <Table.StatusFilter.Option
        value={4}
        filterValue="muted"
        count={counts.muted}
        iconSrc="bell-slash"
        iconClassName="text-muted-foreground"
      >
        Muted
      </Table.StatusFilter.Option>
    </Table.StatusFilter>
  )
}

export default UptimeStatusFilter
